import { useState } from "react";
import { useNavigate, Navigate, useLocation, Link } from "react-router-dom";
import { Shield, Sparkles, Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth, formatApiError } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login, completeMfa, register } = useAuth();
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");

  if (user && user !== false) return <Navigate to="/" replace />;

  const search = new URLSearchParams(location.search);
  const oauthError = search.get("error");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (tab === "login") await login(email.trim(), password);
      else await register(email.trim(), password, name.trim() || null);
      navigate("/", { replace: true });
    } catch (e2) {
      const status = e2.response?.status;
      const detailObj = e2.response?.data?.detail;
      // MFA challenge: server returned a short-lived token to finish login.
      if (detailObj && typeof detailObj === "object" && detailObj.code === "MFA_REQUIRED" && detailObj.mfa_token) {
        setMfaToken(detailObj.mfa_token);
        setBusy(false);
        return;
      }
      const detail = formatApiError(detailObj) || e2.message || "Auth failed";
      if (status === 429) {
        setErr(detail || "Too many attempts. Please wait a moment.");
      } else {
        setErr(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  const onMfaSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await completeMfa(mfaToken, mfaCode.trim());
      navigate("/", { replace: true });
    } catch (e2) {
      const detail = formatApiError(e2.response?.data?.detail) || "That code didn't match.";
      setErr(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hero-theme min-h-screen flex flex-col items-center justify-center px-5 py-10">
      <div className="max-w-md w-full">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-slate-900 text-amber-400 flex items-center justify-center ring-4 ring-slate-900/5">
            <Shield className="w-6 h-6" strokeWidth={2.6} />
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-600">My Money Hero</p>
            <h1 className="text-2xl font-black text-slate-900 leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Suit up, hero
            </h1>
          </div>
        </div>

        <div className="bg-white rounded-3xl border-2 border-slate-100 shadow-sm p-6">
          {mfaToken ? (
            <form onSubmit={onMfaSubmit} className="space-y-4" data-testid="mfa-step">
              <div className="text-center">
                <h2 className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Two-factor code</h2>
                <p className="text-sm text-slate-500 mt-1">Open your authenticator app and enter the 6-digit code.</p>
              </div>
              <Input
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                className="h-12 text-center tracking-[0.5em] text-xl"
                data-testid="mfa-code-input"
              />
              {err && <p className="text-sm text-red-600 text-center" data-testid="mfa-error">{err}</p>}
              <Button
                type="submit"
                disabled={busy || mfaCode.length !== 6}
                className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-amber-400 font-black uppercase tracking-wider"
                data-testid="mfa-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify and continue"}
              </Button>
              <button
                type="button"
                onClick={() => { setMfaToken(""); setMfaCode(""); setErr(""); }}
                className="w-full text-xs text-slate-500 hover:text-slate-700 underline"
                data-testid="mfa-back"
              >
                Use a different account
              </button>
            </form>
          ) : (
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-2 w-full bg-slate-100 rounded-xl h-11 p-1">
              <TabsTrigger value="login" className="rounded-lg font-bold" data-testid="login-tab">
                Sign in
              </TabsTrigger>
              <TabsTrigger value="register" className="rounded-lg font-bold" data-testid="register-tab">
                Create account
              </TabsTrigger>
            </TabsList>

            <form onSubmit={onSubmit} className="mt-5 space-y-4">
              <TabsContent value="register" className="m-0 p-0">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Hero name
                  </Label>
                  <Input
                    id="name" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Captain Cashflow" className="h-11 rounded-xl"
                    data-testid="register-name-input"
                  />
                </div>
              </TabsContent>

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Email
                </Label>
                <Input
                  id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="hero@example.com" className="h-11 rounded-xl"
                  data-testid="auth-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Password
                </Label>
                <Input
                  id="password" type="password" required minLength={6}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters" className="h-11 rounded-xl"
                  data-testid="auth-password-input"
                />
              </div>

              {(err || oauthError) && (
                <div className="text-sm font-semibold text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2" data-testid="auth-error">
                  {err || "Google sign-in failed. Please try again."}
                </div>
              )}

              <Button
                type="submit" disabled={busy}
                className="w-full h-12 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black tracking-wide"
                data-testid="auth-submit-button"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : tab === "login" ? "Sign in" : "Create account"}
              </Button>

              {tab === "login" && (
                <div className="text-right">
                  <Link
                    to="/forgot-password"
                    className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              )}
            </form>
          </Tabs>
          )}

          {!mfaToken && (
          <>
          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-100" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">or</span>
            <div className="h-px flex-1 bg-slate-100" />
          </div>

          <Button
            onClick={startGoogleLogin}
            className="w-full h-12 rounded-2xl bg-white hover:bg-slate-50 text-slate-900 border-2 border-slate-200 font-bold tracking-wide flex items-center justify-center gap-2"
            data-testid="google-login-button"
            type="button"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.12A6.6 6.6 0 0 1 5.5 12c0-.74.13-1.46.34-2.12V7.04H2.18a11 11 0 0 0 0 9.92l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            Continue with Google
          </Button>

          </>
          )}
        </div>
      </div>
    </div>
  );
}
